import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsInfoCircle } from "react-icons/bs";
import PriceExplainModal from "./PriceExplainModal";

/**
 * The info icon that sits next to a price and opens its breakdown.
 *
 * The breakdown is fetched only when the icon is clicked — parcel lists render
 * dozens of cards, and a request per card to answer a question nobody asked
 * would cost more than the feature is worth.
 *
 * `fetcher` is supplied by the caller so the same button works on both sides of
 * the two-guard split: the customer panel passes the user axios client, the
 * admin panel its own. Callers that already hold the payload (the landing
 * calculator computes it client-side) pass `explain` instead.
 */
const PriceInfoButton = ({ fetcher, explain: ready, className = "" }) => {
   const { t } = useTranslation();
   const [open, setOpen] = useState(false);
   const [explain, setExplain] = useState(ready || null);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(false);

   const openModal = async () => {
      if (ready) {
         setExplain(ready);
         setOpen(true);
         return;
      }
      setOpen(true);
      if (explain || loading) return;   // already have it — don't refetch
      setLoading(true);
      setError(false);
      try {
         setExplain(await fetcher());
      } catch (e) {
         setError(true);
      } finally {
         setLoading(false);
      }
   };

   return (
      <>
         <button
            type="button"
            title={t("why_this_price")}
            aria-label={t("why_this_price")}
            onClick={(e) => {
               e.stopPropagation();
               openModal();
            }}
            className={`text-teal_600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200
               transition-colors shrink-0 ${className}`}
         >
            <BsInfoCircle className="w-4 h-4" />
         </button>

         {open && (loading || error) && (
            <div
               className="w-screen h-screen bg-white bg-opacity-80 dark:bg-slate-900 dark:bg-opacity-80
                  flex items-center justify-center fixed top-0 left-0 z-50"
               onClick={() => setOpen(false)}
            >
               <div className="bg-white dark:bg-slate-800 dark:text-slate-100 border border-gray-300
                  dark:border-slate-600 shadow-lg rounded-2xl px-6 py-5 text-sm">
                  {loading ? "…" : t("explain_load_error")}
               </div>
            </div>
         )}

         {open && !loading && !error && explain && (
            <PriceExplainModal explain={explain} onClose={() => setOpen(false)} />
         )}
      </>
   );
};

export default PriceInfoButton;
